import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Loader2, ClipboardCheck, Clock, Info, Target, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const COLORS = {
  productive: "#94a3b8",      // slate-400 (matches Power BI grey)
  non_productive: "#334155",  // slate-700
  non_billable: "#0f766e",    // teal-700 accent
};

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-AU", { month: "short" });
};

const fyMonths = (fyLabel) => {
  const m = /^FY(\d{2})$/.exec(fyLabel || "");
  if (!m) return [];
  const endYear = 2000 + Number(m[1]);
  const out = [];
  for (let i = 0; i < 12; i++) {
    const mm = ((6 + i) % 12) + 1;
    const yy = mm >= 7 ? endYear - 1 : endYear;
    out.push(`${yy}-${String(mm).padStart(2, "0")}`);
  }
  return out;
};

const pct = (v) => (v == null ? "—" : `${v.toFixed(2)}%`);
const hrs = (v) => (v == null ? "—" : fmtNumber(Math.round(v * 100) / 100));
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? String(iso).slice(0, 10)
    : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

function UtilCell({ value, target }) {
  if (value == null) return <span className="text-slate-400">—</span>;
  const below = target != null && value < target;
  return (
    <span className={below ? "text-red-600 font-semibold" : "text-emerald-700"}
          title={target != null ? `Target ${target}%` : undefined}>
      {value.toFixed(1)}%
    </span>
  );
}

function TargetsDialog({ open, onClose, targets, employees, onSaved }) {
  const [defaultTarget, setDefaultTarget] = useState("");
  const [ovEmployee, setOvEmployee] = useState("");
  const [ovTarget, setOvTarget] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && targets) setDefaultTarget(String(targets.default_target ?? 75));
  }, [open, targets]);

  const save = async (body) => {
    setSaving(true);
    try {
      await api.put("/hr/employee-performance/targets", body);
      toast.success("Targets updated");
      setOvEmployee(""); setOvTarget("");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save target");
    } finally { setSaving(false); }
  };

  const nameOf = (uid) => employees.find((e) => e.user_id === uid)?.name || uid;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="emp-perf-targets-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Utilisation targets</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs text-slate-500 mb-1">Default target (% productive hours) — applies to everyone without an override</div>
            <div className="flex gap-2">
              <Input value={defaultTarget} onChange={(e) => setDefaultTarget(e.target.value)}
                     type="number" min="1" max="100" className="h-8 w-24"
                     data-testid="emp-perf-default-target-input" />
              <Button size="sm" disabled={saving}
                      data-testid="emp-perf-save-default-target"
                      onClick={() => save({ default_target: parseFloat(defaultTarget) })}>
                Save default
              </Button>
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Per-technician override</div>
            <div className="flex gap-2">
              <Select value={ovEmployee} onValueChange={setOvEmployee}>
                <SelectTrigger className="h-8 flex-1 text-xs" data-testid="emp-perf-override-employee">
                  <SelectValue placeholder="Pick employee…" />
                </SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  {employees.map((e) => (
                    <SelectItem key={e.user_id} value={e.user_id} className="text-xs">{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={ovTarget} onChange={(e) => setOvTarget(e.target.value)}
                     type="number" min="1" max="100" placeholder="%" className="h-8 w-20"
                     data-testid="emp-perf-override-target-input" />
              <Button size="sm" disabled={saving || !ovEmployee || !ovTarget}
                      data-testid="emp-perf-save-override"
                      onClick={() => save({ user_id: ovEmployee, target: parseFloat(ovTarget) })}>
                Set
              </Button>
            </div>
          </div>
          {targets && Object.keys(targets.overrides || {}).length > 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Current overrides</div>
              <ul className="space-y-1">
                {Object.entries(targets.overrides).map(([uid, t]) => (
                  <li key={uid} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                    <span>{nameOf(uid)} — <b>{t}%</b></span>
                    <button className="text-red-500 hover:text-red-700 font-medium"
                            data-testid={`emp-perf-remove-override-${uid}`}
                            onClick={() => save({ user_id: uid, target: null })}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RateBarPanel({ title, icon: Icon, rows, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-4 text-center">No data for this period</div>
      ) : (
        <ul className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
          {rows.map((r) => (
            <li key={r.name} className="flex items-center gap-2 text-xs">
              <span className="w-[110px] truncate text-slate-700">{r.name}</span>
              <div className="flex-1 h-3.5 bg-slate-100 rounded-sm overflow-hidden">
                <div className="h-full bg-slate-700 rounded-sm"
                     style={{ width: `${Math.min(100, r.rate)}%` }} />
              </div>
              <span className="w-[52px] text-right font-mono text-slate-800">{pct(r.rate)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const EmployeePerformanceTab = ({ canEdit }) => {
  const [filters, setFilters] = useState(null);
  const [fy, setFy] = useState(null);
  const [quarter, setQuarter] = useState("ALL");
  const [month, setMonth] = useState("ALL");
  const [weekEnd, setWeekEnd] = useState("all");
  const [weeks, setWeeks] = useState([]);
  const [bu, setBu] = useState("ALL");
  const [employee, setEmployee] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [targets, setTargets] = useState(null);
  const [targetsDlg, setTargetsDlg] = useState(false);
  const pollRef = useRef(null);

  const loadTargets = useCallback(() => {
    api.get("/hr/employee-performance/targets")
      .then(({ data: t }) => setTargets(t)).catch(() => {});
  }, []);

  useEffect(() => { loadTargets(); }, [loadTargets]);

  useEffect(() => {
    api.get("/hr/employee-performance/filters").then(({ data: f }) => {
      setFilters(f);
      setFy((cur) => cur || f.default_fy || f.fys[0]);
    }).catch(() => toast.error("Failed to load performance filters"));
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (!fy) return;
    api.get(`/hr/employee-performance/weeks?fy=${fy}`)
      .then(({ data: w }) => setWeeks(w.weeks || []))
      .catch(() => setWeeks([]));
  }, [fy]);

  const load = useCallback(async () => {
    if (!fy) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ fy, include_archived: includeArchived });
      if (quarter !== "ALL") params.set("quarter", quarter);
      if (month !== "ALL") params.set("month", month);
      if (weekEnd !== "all") params.set("week_end", weekEnd);
      if (bu !== "ALL") params.set("bu", bu);
      if (employee !== "all") params.set("employee_id", employee);
      const { data: d } = await api.get(`/hr/employee-performance/summary?${params}`);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load employee performance");
    } finally { setLoading(false); }
  }, [fy, quarter, month, weekEnd, bu, employee, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const startSync = async () => {
    setSyncing(true);
    try {
      await api.post("/hr/employee-performance/sync", { years: 2 });
      toast.info("AroFlo timesheet sync started — this can take a few minutes");
      pollRef.current = setInterval(async () => {
        const { data: s } = await api.get("/hr/employee-performance/sync/status");
        if (!s.running) {
          clearInterval(pollRef.current);
          setSyncing(false);
          if (s.last_result?.ok) {
            toast.success(`Sync complete — ${fmtNumber(s.last_result.entries || 0)} timesheet entries processed`);
            const { data: f } = await api.get("/hr/employee-performance/filters");
            setFilters(f);
            load();
          } else {
            toast.error(`Sync failed: ${s.last_result?.error || "unknown error"}`);
          }
        }
      }, 4000);
    } catch (e) {
      setSyncing(false);
      toast.error(e.response?.data?.detail || "Failed to start sync");
    }
  };

  const chartData = useMemo(() =>
    (data?.hours_over_time || []).map((m) => ({ ...m, label: monthLabel(m.month) })),
  [data]);

  const rows = data?.employees || [];
  const totals = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="emp-perf-tab">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-lg p-3">
        {[
          { label: "Financial Year", val: fy || "", set: setFy, testid: "emp-perf-fy",
            items: (filters?.fys || []).map((f) => ({ v: f, l: f })) },
          { label: "Quarter", val: quarter, set: setQuarter, testid: "emp-perf-quarter",
            items: [{ v: "ALL", l: "All" }, ...["FQ1", "FQ2", "FQ3", "FQ4"].map((q) => ({ v: q, l: q }))] },
          { label: "Month", val: month, set: setMonth, testid: "emp-perf-month",
            items: [{ v: "ALL", l: "All" }, ...fyMonths(fy).map((m) => ({ v: m, l: `${monthLabel(m)} ${m.slice(0, 4)}` }))] },
          { label: "End of Week", val: weekEnd, set: setWeekEnd, testid: "emp-perf-week",
            items: [{ v: "all", l: "All" }, ...weeks.map((w) => ({ v: w, l: w }))] },
          { label: "Business Unit", val: bu, set: setBu, testid: "emp-perf-bu",
            items: [{ v: "ALL", l: "All" }, ...(filters?.bus || []).map((b) => ({ v: b, l: b }))] },
          { label: "Employee", val: employee, set: setEmployee, testid: "emp-perf-employee",
            items: [{ v: "all", l: "All" }, ...(filters?.employees || [])
              .filter((e) => includeArchived || !e.archived)
              .map((e) => ({ v: e.user_id, l: e.name }))] },
        ].map((f) => (
          <div key={f.label} className="min-w-[130px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">{f.label}</div>
            <Select value={f.val} onValueChange={f.set}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid={f.testid}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {f.items.map((it) => (
                  <SelectItem key={it.v} value={it.v} className="text-xs">{it.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
        <div className="flex items-center gap-2 pb-1.5">
          <Switch checked={includeArchived} onCheckedChange={setIncludeArchived}
                  data-testid="emp-perf-archived-toggle" />
          <span className="text-xs text-slate-600">Include archived</span>
        </div>
        <div className="ml-auto flex items-center gap-2 pb-0.5">
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setTargetsDlg(true)}
                    data-testid="emp-perf-targets-btn">
              <Target className="w-3.5 h-3.5 mr-1.5" /> Targets
            </Button>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={startSync} disabled={syncing}
                    data-testid="emp-perf-sync-btn">
              {syncing
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              {syncing ? "Syncing AroFlo…" : "Sync from AroFlo"}
            </Button>
          )}
        </div>
      </div>

      {data?.data_freshness?.timesheets_stale && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
             data-testid="emp-perf-timesheet-stale-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Timesheets haven't synced from AroFlo since {fmtDate(data.data_freshness.timesheets_synced_at)}.</span>{" "}
            The Daily JSA Completion Rate is JSA days ÷ field days worked, so it needs current timesheets — recent months will look empty until the sync is refreshed.
            {data.data_freshness.jsa_feed_through && (
              <> The JSA feed itself is current (through {fmtDate(data.data_freshness.jsa_feed_through)}).</>
            )}
            {canEdit
              ? " Click \"Sync from AroFlo\" above to refresh. If it fails, the AroFlo timesheet connection may need attention."
              : " Ask an admin to run the AroFlo sync."}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="emp-perf-empty">
          <div className="text-sm text-slate-600 font-medium">No timesheet data for this period</div>
          <div className="text-xs text-slate-400 mt-1">
            {data?.data_freshness?.timesheets_stale
              ? `Timesheets last synced from AroFlo on ${fmtDate(data.data_freshness.timesheets_synced_at)}. ${canEdit ? "Run \"Sync from AroFlo\" to pull current timesheets." : "Ask an admin to run the AroFlo sync."}`
              : (canEdit ? "Click \"Sync from AroFlo\" to pull timesheets for the current financial year." : "Ask an admin to run the AroFlo sync.")}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <div className="xl:col-span-3 space-y-4">
            {/* Hours Over Time */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Hours Over Time</div>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip formatter={(v, n) => [fmtNumber(v), n]} contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="non_billable" name="Non Billable Hours" stackId="h" fill={COLORS.non_billable} />
                    <Bar dataKey="non_productive" name="Non Productive Hours" stackId="h" fill={COLORS.non_productive} />
                    <Bar dataKey="productive" name="Productive Hours" stackId="h" fill={COLORS.productive} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Employee table */}
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="emp-perf-table">
                  <thead>
                    <tr className="bg-slate-700 text-white text-[10px] uppercase tracking-wider">
                      <th className="text-left font-medium px-3 py-2">Employee</th>
                      <th className="text-left font-medium px-2 py-2">BU</th>
                      <th className="text-right font-medium px-2 py-2">Compl. Tasks</th>
                      <th className="text-right font-medium px-2 py-2">Non Billable Hrs</th>
                      <th className="text-right font-medium px-2 py-2">Non Productive Hrs</th>
                      <th className="text-right font-medium px-2 py-2">Productive Hrs</th>
                      <th className="text-right font-medium px-2 py-2">Overtime Hrs</th>
                      <th className="text-right font-medium px-2 py-2">Utilisation</th>
                      <th className="text-right font-medium px-2 py-2">Timesheet on Time</th>
                      <th className="text-right font-medium px-2 py-2">Daily JSA Compl.</th>
                      <th className="text-right font-medium px-3 py-2">JSA Audit Pass</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.user_id} data-testid={`emp-perf-row-${r.user_id}`}
                          className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-1.5 font-medium text-slate-800 whitespace-nowrap">{r.name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.bu}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtNumber(r.tasks)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{hrs(r.non_billable)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{hrs(r.non_productive)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{hrs(r.productive)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{hrs(r.overtime)}</td>
                        <td className="px-2 py-1.5 text-right font-mono" data-testid={`emp-perf-util-${r.user_id}`}>
                          <UtilCell value={r.utilisation_pct} target={r.utilisation_target} />
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">{pct(r.on_time_pct)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{pct(r.jsa_daily_rate)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{pct(r.jsa_audit_pass_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold text-slate-900 border-t-2 border-slate-300"
                        data-testid="emp-perf-total-row">
                      <td className="px-3 py-2">Total</td>
                      <td className="px-2 py-2"></td>
                      <td className="px-2 py-2 text-right font-mono">{fmtNumber(totals.tasks || 0)}</td>
                      <td className="px-2 py-2 text-right font-mono">{hrs(totals.non_billable)}</td>
                      <td className="px-2 py-2 text-right font-mono">{hrs(totals.non_productive)}</td>
                      <td className="px-2 py-2 text-right font-mono">{hrs(totals.productive)}</td>
                      <td className="px-2 py-2 text-right font-mono">{hrs(totals.overtime)}</td>
                      <td className="px-2 py-2 text-right font-mono">
                        <UtilCell value={totals.utilisation_pct} target={totals.utilisation_target} />
                      </td>
                      <td className="px-2 py-2 text-right font-mono">{pct(totals.on_time_pct)}</td>
                      <td className="px-2 py-2 text-right font-mono">{pct(totals.jsa_daily_rate)}</td>
                      <td className="px-3 py-2 text-right font-mono">{pct(totals.jsa_audit_pass_rate)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex items-start gap-2 text-[11px] text-slate-400 px-1">
              <Info className="w-3.5 h-3.5 mt-[1px] shrink-0" />
              <span>
                Hours &amp; tasks sync from AroFlo timesheets. "Timesheet on Time" = entries logged by end of next day.
                JSA metrics arrive via Import Hub feeds (recipes: <span className="font-mono">jsa_daily_completion</span>, <span className="font-mono">jsa_audit_results</span>) —
                they show "—" until those feeds are set up.
              </span>
            </div>
          </div>

          {/* Side panels */}
          <div className="space-y-4">
            <RateBarPanel title="Daily JSA Completion Rate by Employee" icon={ClipboardCheck}
                          rows={data?.panels?.jsa_daily || []} testid="emp-perf-jsa-panel" />
            <RateBarPanel title="Timesheet Completed on Time by Employee" icon={Clock}
                          rows={data?.panels?.on_time || []} testid="emp-perf-ontime-panel" />
          </div>
        </div>
      )}
      <TargetsDialog open={targetsDlg} onClose={() => setTargetsDlg(false)}
                     targets={targets}
                     employees={(filters?.employees || []).filter((e) => !e.archived)}
                     onSaved={() => { loadTargets(); load(); }} />
    </div>
  );
};
